import { Avatar } from "antd";
import { useEffect, useRef } from "react";
import {
  FiBriefcase,
  FiCalendar,
  FiGrid,
  FiLayers,
  FiLayout,
  FiLogOut,
  FiSettings,
  FiShield,
  FiTool,
  FiUser,
  FiMessageSquare
} from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import { logoutUser } from "../../firebase/utility";
import { setLayout, setUser } from "../../store/actions/actions";
import { syncPermissions } from "../../utils/permissions";


const iconMap = {
  Profile: <FiUser size={15} />,
  Settings: <FiSettings size={15} />,
  "User Management Settings": <FiSettings size={15} />,
  "System Settings": <FiTool size={15} />,
  Categories: <FiGrid size={15} />,
  "Sub Categories": <FiLayers size={15} />,
  "Product Variant/Groups": <FiLayers size={15} />,

  "Allowed Manufacturers": <FiShield size={15} />,
  "DNH Jobs": <FiBriefcase size={15} />,
  Reviews: <FiMessageSquare size={15} />,
};

const ProfileDropdown = ({ items, isOpen, toggleDropdown }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { layout } = useSelector((state) => state.redux);
  const dropdownRef = useRef(null);

  // Filter menu items based on user role
  const filteredItems = items.filter((item) => {
    const role = user?.role?.toLowerCase() || "";
    if (role === "super-admin") return true;

    // For admin/user, check if the item is meant to be shown
    if (role === "admin" ? item.show.includes("admin") : item.show.includes("user")) {
      // If it's just 'Profile', allow it
      if (item.label === "Profile") return true;

      // Otherwise check specific permission for the item
      const syncedPermissions = syncPermissions(user?.permissions);
      const targetPermissionName = item.permissionName || item.label;
      const permission = syncedPermissions.find(p => p.name === targetPermissionName);
      return permission ? permission.value : false;
    }
    return false;
  });

  const toggleLayout = () => {
    dispatch(setLayout(layout === "sidebar" ? "navbar" : "sidebar"));
  };

  // Click-outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        if (isOpen) toggleDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "U";

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      {/* Avatar Trigger */}
      <div
        onClick={toggleDropdown}
        className="pointer d-flex align-items-center gap-2"
        style={{ userSelect: "none" }}
      >
        <Avatar
          src={user?.profileImage}
          aria-haspopup="true"
          aria-expanded={isOpen}
          size={38}
          style={{
            backgroundColor: user?.profileImage ? "transparent" : "#00509d",
            color: "#fff",
            fontWeight: "700",
            fontSize: "14px",
            border: "2px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(75,108,183,0.25)",
          }}
        >
          {!user?.profileImage && initials}
        </Avatar>

      </div>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 10px)",
            right: 0,
            width: "240px",
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
            border: "1px solid #e8edf5",
            zIndex: 1050,
            overflow: "hidden",
          }}
        >
          {/* User Info Header */}
          <div style={{ padding: "16px", borderBottom: "1px solid #e8edf5", backgroundColor: "#f8fafc" }}>
            <div style={{ fontSize: "15px", fontWeight: "600", color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {user?.username || "User"}
            </div>
            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px", textTransform: "capitalize" }}>
              {user?.role || "Member"}
            </div>
          </div>


          {/* Layout Toggle */}
          <div style={{ padding: "8px 4px 4px" }}>
            <div style={{ padding: "4px 12px 6px", fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.8px" }}>
              Display
            </div>
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ padding: "8px 12px", borderRadius: "8px", margin: "0 4px" }}
            >
              <div className="d-flex align-items-center gap-2">
                <FiLayout size={15} style={{ color: "#00509d" }} />
                <span style={{ fontSize: "13px", fontWeight: "500", color: "#334155" }}>Navbar Layout</span>
              </div>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input pointer"
                  type="checkbox"
                  role="switch"
                  checked={layout === "navbar"}
                  onChange={toggleLayout}
                  style={{ width: "36px", height: "18px", cursor: "pointer" }}
                />
              </div>
            </div>
          </div>

          <div style={{ height: "1px", backgroundColor: "#f1f5f9", margin: "4px 12px" }} />

          {/* Nav Items */}
          <div style={{ padding: "4px" }}>
            <div style={{ padding: "4px 12px 6px", fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.8px" }}>
              Navigation
            </div>
            {filteredItems.map((item, index) => (
              <Link
                key={index}
                to={item.href}
                onClick={toggleDropdown}
                style={{ textDecoration: "none" }}
              >
                <div
                  className="d-flex align-items-center gap-2"
                  style={{
                    padding: "8px 12px",
                    borderRadius: "8px",
                    margin: "1px 4px",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#334155",
                    transition: "all 0.15s",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#f1f5f9"; e.currentTarget.style.color = "#00509d"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#334155"; }}
                >
                  <span style={{ color: "#00509d", display: "flex" }}>{iconMap[item.label] || <FiGrid size={15} />}</span>
                  {item.label}
                </div>
              </Link>
            ))}

            <a
              href="https://spearhead-solutions-lms.netlify.app/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={toggleDropdown}
              style={{ textDecoration: "none" }}
            >
              <div
                className="d-flex align-items-center gap-2"
                style={{ padding: "8px 12px", borderRadius: "8px", margin: "1px 4px", fontSize: "13px", fontWeight: "500", color: "#334155", cursor: "pointer", transition: "all 0.15s" }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#f1f5f9"; e.currentTarget.style.color = "#00509d"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#334155"; }}
              >
                <span style={{ color: "#00509d", display: "flex" }}><FiCalendar size={15} /></span>
                Leave Management
              </div>
            </a>
          </div>

          {/* Logout */}
          <div style={{ padding: "8px", borderTop: "1px solid #f1f5f9" }}>
            <div
              className="d-flex align-items-center gap-2 pointer"
              style={{
                padding: "9px 12px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "600",
                color: "#ef4444",
                backgroundColor: "#fef2f2",
                border: "1px solid #fee2e2",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#fee2e2"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#fef2f2"; }}
              onClick={() => {
                logoutUser().then((res) => {
                  if (res) dispatch(setUser({}));
                });
              }}
            >
              <FiLogOut size={15} />
              Sign Out
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
